/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/no-static-element-interactions */
'use client';

import classNames from 'classnames';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Button from '../../components/Button';
import ChevronRight from '../../components/icons/ChevronRight';
import { InlineTooltip } from '../../components/InlineTooltip';
import Tooltip from '../../components/Tooltip';
import { CmsProduct } from '../../types/cmsProduct';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { fastHash } from '../../utils/hash';
import { JsonObject } from '../../utils/json';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { productSortKey } from '../SortAndFilterPanel/productSortKey';
import {
  ComparisonTableBuilderProps,
  ComparisonTableBuilderColumn,
} from './ComparisonBuilderInterface';
import {
  getColumnHiddenBreakpointsClasses,
  getResponsiveTableTemplates,
  responsiveTableGrids,
} from './ComparisonTableHelpers';
import { TableEdgeFade } from './components/tableEdgeFade';
import { TableRuleButton } from './components/tableRuleButton';
import { TRANSLATIONS } from './i18n';
import { getTooltipComponentTitle } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComparisonTableBuilder: FC<
  ComparisonTableBuilderProps<CmsProduct>
> = (props) => {
  const {
    data,
    slug,
    columns,
    showMore,
    onRowClick,
    isRowSelected,
    translations,
    context,
    pushDataLayer,
    footer,
    getRowClassNames,
  } = props;

  const tableId = `table-comparison-${data.length}-length`;
  const staticText = getClientTranslations(
    { ...TRANSLATIONS, ...translations },
    context,
  );

  const [showAll, setShowAll] = useState(false);
  // Only a forward arrow: once the user has scrolled right they can scroll back, and a back arrow
  // over the sticky columns covered content (CMS-1906 design review).
  const [showRight, setShowRight] = useState(false);
  // No back arrow, but the left edge fade still marks content scrolled out of view on the left.
  const [showLeft, setShowLeft] = useState(false);
  const [top, setwTop] = useState(0);
  const setTop = useDebouncedCallback(setwTop, 50);
  const [campaignTopHeight, setCampaignStickyTop] = useState(0);
  const [personalLoanFilterHeight, setPersonalLoanFilterHeight] = useState(0);
  const [tableTabsHeight, setTableTabsHeight] = useState(0);

  const refId = useRef<number | null>(null);

  const { helpLogic, scroll: scrollByBreakpoint } = useBreakpoint<{
    helpLogic: 'click' | 'hover';
    scroll: boolean;
  }>({
    _: { helpLogic: 'click', scroll: true },
    md: { helpLogic: 'click', scroll: true },
    lg: { helpLogic: 'click', scroll: true },
    xl: { helpLogic: 'click', scroll: true },
    xxl: { helpLogic: 'hover', scroll: false },
  });
  const tableRef = useRef<HTMLDivElement | null>(null);

  // Measured rather than assumed from the breakpoint: in a column beside the
  // panel the fixed column widths can exceed the container, and `overflow` does
  // nothing on a `display: table`, so the excess pushed the whole page sideways.
  const [overflowsContainer, setOverflowsContainer] = useState(false);
  const scroll = scrollByBreakpoint || overflowsContainer;

  useEffect(() => {
    const el = tableRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    // Neither width changes when the variant does — scrollbars are hidden — so
    // this settles in one step instead of oscillating.
    const measure = () =>
      setOverflowsContainer(el.scrollWidth > el.clientWidth + 1);

    measure();
    const observer = new ResizeObserver(measure);
    // Show more changes the content width without touching the container's.
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    // The child is captured above, so a replacement of that node would go
    // unobserved; this re-attaches when the subtree changes.
    const mutations = new MutationObserver(() => {
      measure();
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    });
    mutations.observe(el, { childList: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [columns.length]);

  useEffect(() => {
    const stickyIds = [
      'top-page-sticky-content',
      'personal-loan-filter',
      'comparison-table-tabs',
    ];
    const stickySetFUncs = [
      setCampaignStickyTop,
      setPersonalLoanFilterHeight,
      setTableTabsHeight,
    ];

    stickyIds.forEach((id, index) => {
      const func = stickySetFUncs[index];
      const stickyContentElement = document.getElementById(id);
      let resizeObserver: ResizeObserver | null = null;
      let mutationObserver: MutationObserver | null = null;

      const checkHeight = () => {
        if (stickyContentElement) {
          const height = stickyContentElement.className.includes('sticky')
            ? stickyContentElement.offsetHeight
            : 0;
          func(height);
        }
      };

      if (stickyContentElement) {
        resizeObserver = new ResizeObserver(checkHeight);
        resizeObserver.observe(stickyContentElement);

        mutationObserver = new MutationObserver(checkHeight);
        mutationObserver.observe(stickyContentElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
    });
  }, []);

  useEffect(() => {
    if (scroll) {
      setTop(0);
      return;
    }
    setTop(personalLoanFilterHeight + tableTabsHeight + campaignTopHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignTopHeight, personalLoanFilterHeight, tableTabsHeight, scroll]);

  const {
    showMoreCount,
    showMoreRender,
    showMoreText = staticText.show_all_cards,
  } = showMore ?? {};

  const { visibleData, hiddenData, isShowMoreAvailable, allData } =
    useMemo(() => {
      const filteredData = data.filter(Boolean);
      const isShowMoreAvailable = Boolean(
        showMoreCount && filteredData.length > showMoreCount,
      );

      const visibleData =
        !showAll && isShowMoreAvailable
          ? filteredData.slice(0, showMoreCount)
          : filteredData;

      const hiddenData =
        filteredData.length > visibleData.length
          ? filteredData.slice(visibleData.length)
          : [];

      return {
        visibleData,
        hiddenData,
        isShowMoreAvailable,
        allData: filteredData,
      };
    }, [data, showMoreCount, showAll]);

  const isShowMoreVisible = !showAll && isShowMoreAvailable;

  // A `pinned` first column (the CMS-1906 rail) sticks in front of the table's own sticky first
  // column, which is then offset by the rail's track width so the two never overlap or gap.
  const railColumn = columns[0]?.pinned ? columns[0] : undefined;
  const pinnedCount = railColumn ? 2 : 1;
  const pinnedClasses = (index: number) => ({
    sticky: index < pinnedCount,
    'left-0': index === 0,
  });
  const pinnedStyle = (index: number) =>
    railColumn && index === 1
      ? { left: railColumn.columnSizes?.sm }
      : undefined;

  const responsiveTableTemplates = getResponsiveTableTemplates(columns);

  useEffect(() => {
    if (!tableRef.current) {
      return;
    }
    // sub-pixel overflow is not scrollable content - no arrow for it
    if (tableRef.current.scrollWidth > tableRef.current.clientWidth + 1) {
      setShowRight(true);
    }
  }, [tableRef, scroll]);

  const checkSize = () => {
    if (refId.current) {
      cancelAnimationFrame(refId.current);
    }

    refId.current = requestAnimationFrame((e) => {
      const t = tableRef?.current?.getBoundingClientRect();
      const offsetHeight =
        campaignTopHeight + personalLoanFilterHeight + tableTabsHeight;
      const needZeroTop = tableRef.current && t && t?.top > offsetHeight;
      const newTop = needZeroTop ? 0 : -(t?.top ?? 0) + offsetHeight - 1;
      setTop(newTop);

      return e;
    });
  };

  useEffect(() => {
    if (!scroll) {
      setTop(personalLoanFilterHeight + campaignTopHeight + tableTabsHeight);

      return;
    }

    const resizeHandler = () => {
      checkSize();
    };
    const scrollHandler = () => checkSize();

    window.addEventListener('resize', resizeHandler);
    window.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('scroll', scrollHandler);
      if (refId.current) cancelAnimationFrame(refId.current);
    };
  }, [
    tableRef.current,
    scroll,
    personalLoanFilterHeight,
    tableTabsHeight,
    campaignTopHeight,
  ]);

  const updateScrollEdges = useCallback((t: HTMLDivElement) => {
    const offsetThrottle = 10;
    setShowLeft(!!t.scrollLeft);
    setShowRight(
      t.clientWidth + t.scrollLeft <= t.scrollWidth - offsetThrottle,
    );
  }, []);

  const tableScrollHandler = useCallback(
    (e: { currentTarget: HTMLDivElement }) =>
      updateScrollEdges(e.currentTarget),
    [updateScrollEdges],
  );

  // A resize can create or remove the overflow without any scroll event.
  useEffect(() => {
    const onResize = () => {
      if (tableRef.current) {
        updateScrollEdges(tableRef.current);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updateScrollEdges]);

  const styleToThead = useMemo(() => {
    if (!scroll) {
      return {
        top,
      };
    }

    return {
      transform: `translateY(${top}px)`,
      willChange: 'transform',
    };
  }, [scroll, top]);
  if (!visibleData.length) {
    return (
      <div
        className={classNames(
          'rounded-lg border border-borders/default',
          'px-spacing/gap/soft-12px py-spacing/padding/y',
          'min-h-line/height-table-cell bg-fill/card',
          'flex flex-col items-center justify-center gap-1 text-center',
        )}
      >
        <h3 className="mhg-alpha-typography-bullet-1 text-colors/brand-default-primary-dark">
          {staticText.no_personal_loans_title}
        </h3>
        <span
          style={{ color: '#424242' }}
          className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell"
        >
          {staticText.no_personal_loans_subtitle}
        </span>
      </div>
    );
  }
  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="relative">
      <div
        className={classNames(
          'scrollbar-none relative flex flex-col',
          // overflow-hidden break sticky header, so we use
          // - js hack on mobile to fix header and radius on table wrapper
          // - on desktop we use css only for sticky header but radius is used for first and last th/td (it works because there is no scrollbar on desktop)
          'overflow-hidden rounded-radius/table border border-borders/default',
          // Unclipping is what allows the CSS-only sticky header.
          { 'lg:overflow-visible': !scroll },
        )}
      >
        <div
          ref={tableRef}
          // Scrolls horizontally despite the `-y`: the other axis computes from
          // `visible` to `auto` beside it, and the handler drives `scrollLeft`.
          className={classNames('scrollbar-none ', {
            ' overflow-y-auto': !!scroll,
          })}
          onScroll={tableScrollHandler}
        >
          <table
            id={tableId}
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className={classNames(
              'w-full max-w-full',
              'group',
              'relative',
              'flex-1',
              'overflow-x-auto lg:overflow-x-hidden',
              'scrollbar-none',
            )}
            style={{
              ...responsiveTableTemplates,
              maxWidth: '100%',
            }}
          >
            <thead className="sticky z-30" style={styleToThead}>
              <tr
                key="tr_head"
                className={classNames(
                  'grid rounded-t-radius/table',
                  ...responsiveTableGrids,
                )}
              >
                {columns.map((column, index) => (
                  <th
                    key={fastHash({
                      header: column.header,
                      field: column.field,
                      index,
                    })}
                    style={pinnedStyle(index)}
                    // eslint-disable-next-line tailwindcss/no-arbitrary-value
                    className={classNames([
                      'bg-fill/alternative-1',
                      'z-30',
                      'min-h-table/cell-min-height p-spacing/gap/soft-12px',
                      pinnedClasses(index),
                      {
                        'border-l': index >= pinnedCount,
                        'z-40': index < pinnedCount,
                        'rounded-tl-radius/table': index === 0,
                        'rounded-tr-radius/table': index === columns.length - 1,
                      },
                      getColumnHiddenBreakpointsClasses(
                        column.hiddenBreakpoints,
                      ),
                      column.getHeaderClassNames?.(column, index),
                    ])}
                  >
                    <div
                      className={classNames('flex h-full items-center gap-1')}
                    >
                      <div
                        className={classNames(
                          'font-font-weight/table-header text-font-size/table-header leading-line-height/table-header',
                          'text-text/secondary',
                          'whitespace-break-spaces text-left',
                        )}
                      >
                        {column.header?.replace(/\\n/g, '\u000A')}
                      </div>
                      {column.headerInfo && (
                        <Button
                          variant="text"
                          className="p-1"
                          onClick={() => {
                            if (column.headerInfoTrackingTitle) {
                              pushDataLayer({
                                event: 'navigate',
                                component_type: 'tooltip',
                                component_title: column.headerInfoTrackingTitle,
                                component_index: '',
                                action: 'click',
                                index: '',
                              });
                            } else {
                              pushDataLayer({
                                event: 'interact',
                                location: 'comparison-table-head',
                                component_type: 'tooltip',
                                component_title: column.headerInfo,
                                action: 'click',
                              });
                            }
                            column.onHeaderInfoClick?.();
                          }}
                        >
                          <Tooltip
                            onOpenEvent="hover"
                            infoClass="!text-text/disabled !size-4 md:!size-[18px] lg:!size-[18px]"
                            content={column.headerInfo}
                            holderClasses={['ml-1']}
                            tooltipPaddingClass="px-spacing/padding/x-sm py-spacing/padding/soft-12px"
                          />
                        </Button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allData.map((datum, rowIndex) => {
                const isLastRow =
                  (!isShowMoreVisible &&
                    showMoreCount &&
                    rowIndex === showMoreCount - 1) ||
                  rowIndex === allData.length - 1;
                const isSelected = isRowSelected?.(datum, rowIndex);
                return (
                  <tr
                    data-uid={datum.uid}
                    data-title={datum.title}
                    // Keyed by product so a sort moves each row's own node;
                    // `?? rowIndex` because both ids are nullable upstream, as
                    // in `QuickAnswer.tsx:428`.
                    key={productSortKey(datum) ?? rowIndex}
                    className={classNames(
                      {
                        hidden:
                          !showAll &&
                          showMoreCount &&
                          rowIndex >= showMoreCount,
                        'rounded-bl-radius/table rounded-br-radius/table':
                          isLastRow,
                      },
                      'grid',
                      // 'grid-cols-[var(--table-template-cols)]',
                      ...responsiveTableGrids,
                      'group/row z-40 lg:hover:bg-fill/base',
                      getRowClassNames?.(datum, rowIndex),
                    )}
                  >
                    {columns.map((column, colIndex, colSelf) => {
                      const helpText = column.renderHelp
                        ? column.renderHelp(datum)
                        : null;

                      return (
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                        <td
                          key={`${datum.uid}-${fastHash({ colIndex })}`}
                          onClick={(e) =>
                            column?.onColumnClick
                              ? column?.onColumnClick(e, datum, rowIndex)
                              : onRowClick?.(datum, rowIndex)
                          }
                          style={pinnedStyle(colIndex)}
                          className={classNames(
                            'bg-white group-hover/row:bg-fill/base',
                            'border-t border-borders/default',
                            'z-10',
                            'min-h-table/cell-min-height p-spacing/gap/soft-12px',
                            pinnedClasses(colIndex),
                            {
                              'z-20': colIndex < pinnedCount,
                              'cursor-pointer': !!onRowClick,
                              '!bg-fill/primary-light':
                                isSelected &&
                                !column.keepsOwnBackground?.(datum),
                              'rounded-bl-radius/table':
                                isLastRow && colIndex === 0,
                              'rounded-br-radius/table':
                                isLastRow && colIndex === colSelf.length - 1,
                            },
                            getColumnHiddenBreakpointsClasses(
                              column.hiddenBreakpoints,
                            ),
                            column.getCellClassNames
                              ? column.getCellClassNames(
                                  datum,
                                  column,
                                  rowIndex,
                                  colIndex,
                                )
                              : null,
                          )}
                        >
                          <div
                            className={classNames(
                              'flex h-full items-center',
                              column.getCellDivClassNames
                                ? column.getCellDivClassNames(
                                    datum,
                                    column,
                                    rowIndex,
                                    colIndex,
                                  )
                                : null,
                            )}
                          >
                            <CellContent
                              slug={slug}
                              column={column}
                              datum={datum}
                              rowIndex={rowIndex}
                              colIndex={colIndex}
                              helpText={helpText}
                              helpLogic={helpLogic}
                              pushDataLayer={pushDataLayer}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isShowMoreVisible && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            style={{
              width: '100%',
            }}
            className="col-span-full cursor-pointer overflow-hidden rounded-b-radius/table border-t border-borders/default p-2 bg-white hover:bg-fill/base md:p-3"
            onClick={() => {
              setShowAll(true);
              pushDataLayer({
                event: 'navigate',
                component_type: 'accordion',
                component_title: 'Product List/Show more Cards',
                action: 'expand',
                index: 0,
              });
            }}
          >
            <div className="flex flex-row items-center gap-2">
              <div className="mr-2 flex h-auto flex-none items-center gap-1 px-0 font-font-weight/bullet-2 text-buttons/neutral-dark alpha-bullet-2 text-font-size/bullet-2">
                <ChevronRight className="size-6" />
                <p className="mhg-alpha-typography-bullet-2">
                  {showMoreText} ({data.length})
                </p>
              </div>
              {showMoreRender &&
                hiddenData.map((datum, index) => (
                  <div
                    key={fastHash({
                      uid: datum.uid,
                      product_name: datum.product_name,
                      index,
                    })}
                  >
                    {showMoreRender(datum, index)}
                  </div>
                ))}
            </div>
          </div>
        )}
        {footer}
        {/* Only for the measured overflow: at the always-scrolling breakpoints
            the table has had `TableRuleButton` as its affordance all along, and
            those pages are not what this ticket changes. Inside the wrapper so
            the rounded corners and the border clip it. */}
        <TableEdgeFade side="left" show={showLeft && overflowsContainer} />
        <TableEdgeFade side="right" show={showRight && overflowsContainer} />
      </div>
      <TableRuleButton
        type="right"
        onClick={() => {
          tableRef.current?.scrollTo({
            left: 10000,
            top: 0,
            behavior: 'smooth',
          });
        }}
        show={showRight && scroll}
      />
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface CellContentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: ComparisonTableBuilderColumn<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datum: any;
  rowIndex: number;
  colIndex: number;
  helpText: string | null | undefined;
  helpLogic: 'click' | 'hover';
  pushDataLayer: (data: JsonObject) => void;
  slug?: string;
}

const CellContent = ({
  column,
  datum,
  rowIndex,
  colIndex,
  helpText,
  helpLogic,
  pushDataLayer,
  slug,
}: CellContentProps) => {
  const renderHelp = (isInline: boolean) =>
    helpText ? (
      <InlineTooltip
        placement="top"
        onOpenEvent={helpLogic}
        content={helpText}
        infoClassName={classNames('text-secondary-lighter', {
          inline: isInline,
          'absolute bottom-0 right-0 bg-white group-hover/row:bg-fill/base pl-1':
            !isInline,
        })}
        onClick={() => {
          const componentTitle = getTooltipComponentTitle(
            column.header,
            helpText,
          );
          sendProductAnalytics({
            action: UserActionEnum.CLICK,
            componentTitle,
            componentType: ComponentTypeEnum.TOOLTIP,
            event: 'product_interaction',
            index: rowIndex,
            product: datum as CmsProduct,
            pushDataLayer,
            url: slug,
          });
        }}
        onHover={() => {
          if (helpLogic !== 'hover') {
            return;
          }
          const componentTitle = getTooltipComponentTitle(
            column.header,
            helpText,
          );
          sendProductAnalytics({
            action: UserActionEnum.HOVER,
            componentTitle,
            componentType: ComponentTypeEnum.TOOLTIP,
            event: 'product_interaction',
            index: rowIndex,
            product: datum as CmsProduct,
            pushDataLayer,
            url: slug,
          });
        }}
      />
    ) : null;

  const renderCell = (children: ReactNode) => {
    return (
      <div
        className={classNames(
          'line-clamp-2 leading-line-height/table-cell',
          getCellClassNames(column),
          column.getCellDivContentClassNames
            ? column.getCellDivContentClassNames(
                datum,
                column,
                rowIndex,
                colIndex,
              )
            : null,
          !!helpText && 'relative',
        )}
      >
        {children}
      </div>
    );
  };

  if (column.render) {
    return column.render(
      datum,
      column,
      rowIndex,
      colIndex,
      renderHelp(true),
      renderCell,
    );
  }

  return renderCell(column.field ? (datum[column.field] ?? '') : '');
};

const getCellClassNames = (
  column: ComparisonTableBuilderColumn<CmsProduct>,
): string => {
  const style = column.style ?? 'REGULAR';

  return classNames({
    'py-spacing/padding/soft-4px px-spacing/padding/soft-8px bg-fill/primary-light text-fill/primary-dark font-font-weight/table-cell-strong rounded-radius/tooltip':
      style === 'HIGHLIGHT',
    'text-fill/primary-dark font-font-weight/table-cell-strong':
      style === 'STRONG_COLOUR',
    'text-text/primary font-font-weight/table-cell-strong': style === 'STRONG',
    'text-text/primary font-font-weight/table-cell-normal': style === 'REGULAR',
  });
};
